import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import SPPDMap from '@/components/SPPDMap';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, MapPin, Plane, Calendar, Briefcase, User, FileText, CheckCircle2, XCircle, PlayCircle, Flag, Globe, CreditCard } from 'lucide-react';
import { fmtDate, fmtDateTime, fmtCurrency } from '@/lib/dates';
import { toast } from 'sonner';
import { fileUrl } from '@/components/Uploaders';

export default function SPPDDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sppd, setSppd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, type: '' });
  const [reason, setReason] = useState('');

  const load = () => {
    setLoading(true);
    api.get(`/sppd/${id}`).then((r) => setSppd(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const action = async (path, body, ok) => {
    try {
      await api.post(`/sppd/${id}/${path}`, body || {});
      toast.success(ok);
      setModal({ open: false, type: '' });
      setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    }
  };

  if (loading || !sppd) return <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>;

  const isKabag = user?.role === 'admin' || user?.role === 'kabag';
  const isOps = user?.role === 'admin' || user?.role === 'operator' || user?.role === 'kabag';

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        title={sppd.nomor_sppd}
        subtitle={sppd.jenis_perjalanan === 'Luar Negeri'
          ? `${sppd.pejabat_nama} → ${sppd.kota_luar_negeri}, ${sppd.negara_tujuan}`
          : `${sppd.pejabat_nama} → ${sppd.tujuan_kota}, ${sppd.tujuan_provinsi}`}
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'SPPD', to: '/sppd' }, { label: 'Detail' }]}
        action={<Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" />Kembali</Button>}
      />

      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-5 flex flex-wrap items-center gap-3">
          <StatusBadge status={sppd.status} type="sppd" />
          {sppd.jenis_perjalanan === 'Luar Negeri' ? (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-700 font-bold"><Globe className="w-3 h-3" /> Luar Negeri</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-bold"><MapPin className="w-3 h-3" /> Dalam Negeri</span>
          )}
          <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{sppd.transportasi}</span>
          <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
            {isKabag && sppd.status === 'Pending' && (
              <>
                <Button data-testid="sppd-approve" onClick={() => setModal({ open: true, type: 'approve' })} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-2" />Setujui</Button>
                <Button data-testid="sppd-reject" onClick={() => setModal({ open: true, type: 'reject' })} variant="destructive"><XCircle className="w-4 h-4 mr-2" />Tolak</Button>
              </>
            )}
            {isOps && sppd.status === 'Approved' && (
              <Button onClick={() => action('status', { status: 'Sedang Dinas' }, 'Pejabat berangkat dinas')} className="bg-sky-600 hover:bg-sky-700"><PlayCircle className="w-4 h-4 mr-2" />Catat Keberangkatan</Button>
            )}
            {isOps && sppd.status === 'Sedang Dinas' && (
              <Button onClick={() => action('status', { status: 'Selesai' }, 'Pejabat kembali, SPPD selesai')} className="bg-emerald-600 hover:bg-emerald-700"><Flag className="w-4 h-4 mr-2" />Catat Kepulangan</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold">Informasi SPPD</CardTitle></CardHeader>
            <CardContent className="divide-y divide-slate-100">
              <InfoRow icon={User} label="Pejabat" value={`${sppd.pejabat_nama} — ${sppd.jabatan}`} />
              <InfoRow icon={Briefcase} label="OPD" value={sppd.opd_nama || '-'} />
              {sppd.jenis_perjalanan === 'Luar Negeri' ? (
                <>
                  <InfoRow icon={Globe} label="Negara Tujuan" value={sppd.negara_tujuan || '-'} />
                  <InfoRow icon={MapPin} label="Kota Tujuan" value={sppd.kota_luar_negeri || '-'} />
                  {sppd.instansi_tujuan && <InfoRow icon={Briefcase} label="Instansi Tujuan" value={sppd.instansi_tujuan} />}
                  {sppd.nomor_paspor && <InfoRow icon={CreditCard} label="Nomor Paspor" value={sppd.nomor_paspor} />}
                  <InfoRow icon={CreditCard} label="Mata Uang Biaya" value={sppd.mata_uang_biaya || 'IDR'} />
                </>
              ) : (
                <InfoRow icon={MapPin} label="Tujuan" value={`${sppd.tujuan_kota}, ${sppd.tujuan_provinsi}`} />
              )}
              <InfoRow icon={Calendar} label="Periode" value={`${fmtDate(sppd.tanggal_berangkat)} - ${fmtDate(sppd.tanggal_kembali)}`} />
              <InfoRow icon={Plane} label="Transportasi" value={sppd.transportasi} />
              <InfoRow icon={FileText} label="Sumber Dana" value={sppd.sumber_dana || '-'} />
              <InfoRow icon={FileText} label="Biaya Estimasi" value={sppd.jenis_perjalanan === 'Luar Negeri' && sppd.mata_uang_biaya !== 'IDR' ? `${sppd.mata_uang_biaya} ${(sppd.biaya_estimasi || 0).toLocaleString('id-ID')}` : fmtCurrency(sppd.biaya_estimasi)} />
              <div className="py-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Keperluan</div>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{sppd.keperluan}</p>
              </div>
              {sppd.keterangan_tambahan && (
                <div className="py-3">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Keterangan Tambahan</div>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{sppd.keterangan_tambahan}</p>
                </div>
              )}
              {sppd.pendamping_nama?.length > 0 && (
                <div className="py-3">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Pendamping</div>
                  <div className="flex flex-wrap gap-1.5">
                    {sppd.pendamping_nama.map((p, i) => <span key={i} className="px-2 py-0.5 rounded bg-slate-100 text-xs font-medium">{p}</span>)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><MapPin className="w-4 h-4 text-[#DC2626]" />Peta Tujuan</CardTitle></CardHeader>
            <CardContent>
              <SPPDMap items={[sppd]} height={320} />
            </CardContent>
          </Card>

          {sppd.dokumen?.length > 0 && (
            <Card className="border-slate-200 shadow-premium">
              <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><FileText className="w-4 h-4 text-[#DC2626]" />Dokumen SPPD</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {sppd.dokumen.map((d, i) => (
                    <a key={i} href={fileUrl(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 rounded border border-slate-200 hover:border-red-300 hover:bg-red-50/30">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="flex-1 text-sm font-medium text-slate-700">{d.name}</span>
                      {d.kind && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{d.kind}</span>}
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <Card className="border-slate-200 shadow-premium h-fit">
          <CardHeader><CardTitle className="text-base font-bold">Riwayat Perjalanan</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Line title="Diajukan" time={fmtDateTime(sppd.created_at)} color="slate" icon={FileText} />
            {sppd.approved_at && <Line title="Disetujui Kabag" time={fmtDateTime(sppd.approved_at)} color="emerald" icon={CheckCircle2} />}
            {sppd.departure_logged_at && <Line title="Berangkat" time={fmtDateTime(sppd.departure_logged_at)} color="sky" icon={PlayCircle} />}
            {sppd.return_logged_at && <Line title="Kembali / Selesai" time={fmtDateTime(sppd.return_logged_at)} color="emerald" icon={Flag} />}
            {sppd.status === 'Rejected' && <Line title="Ditolak" subtitle={sppd.rejection_reason} time={fmtDateTime(sppd.updated_at)} color="red" icon={XCircle} />}
          </CardContent>
        </Card>
      </div>

      <Dialog open={modal.open} onOpenChange={(o) => setModal({ ...modal, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{modal.type === 'approve' ? 'Setujui SPPD' : 'Tolak SPPD'}</DialogTitle></DialogHeader>
          <Textarea placeholder={modal.type === 'approve' ? 'Catatan (opsional)' : 'Alasan penolakan'} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false, type: '' })}>Batal</Button>
            {modal.type === 'approve'
              ? <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => action('approve', { catatan: reason }, 'SPPD disetujui')}>Setujui</Button>
              : <Button variant="destructive" onClick={() => action('reject', { catatan: reason }, 'SPPD ditolak')}>Tolak</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="py-3 flex items-start gap-3">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5" />
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="text-sm text-slate-800 font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function Line({ icon: Icon, title, subtitle, time, color = 'slate' }) {
  const colors = { slate: 'bg-slate-100 text-slate-600', emerald: 'bg-emerald-100 text-emerald-700', red: 'bg-red-100 text-red-700', sky: 'bg-sky-100 text-sky-700' };
  return (
    <div className="flex gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colors[color]}`}><Icon className="w-4 h-4" /></div>
      <div className="flex-1">
        <div className="font-semibold text-sm">{title}</div>
        {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
        <div className="text-[11px] text-slate-400 mt-0.5">{time}</div>
      </div>
    </div>
  );
}
